import { createEventEmitter, type Event } from "@core/Event/EventEmitter";
import type ApiUrlCreator from "@core-ui/ApiServices/ApiUrlCreator";
import FetchService from "@core-ui/ApiServices/FetchService";
import MimeTypes from "@core-ui/ApiServices/Types/MimeTypes";
import DefaultError from "@ext/errorHandlers/logic/DefaultError";
import t from "@ext/localization/locale/translate";

type PublishServiceEvents = Event<"start"> &
	Event<"finish"> &
	Event<"error", { error: DefaultError; apiUrlCreator: ApiUrlCreator }>;

export const PublishEmitter = {
	async publish(apiUrlCreator: ApiUrlCreator, message: string, files: string[]): Promise<boolean> {
		await PublishEmitter.events.emit("start", {});

		const endpoint = apiUrlCreator.getStoragePublishUrl(message);
		const res = await FetchService.fetch<DefaultError>(endpoint, JSON.stringify(files), MimeTypes.json);

		if (!res.ok) {
			const resWithError = res as unknown as Response & { error?: DefaultError; body?: DefaultError };
			const errorFromFetch = resWithError.error ?? resWithError.body;
			if (errorFromFetch) {
				await PublishEmitter.events.emit("error", { error: errorFromFetch, apiUrlCreator });
				return false;
			}

			try {
				const error = await res.json();
				await PublishEmitter.events.emit("error", { error, apiUrlCreator });
				return false;
			} catch (e) {
				const error = new DefaultError(
					`${t("git.publish.error.unknown")} ${e instanceof Error ? e.message : String(e)}`,
				);
				await PublishEmitter.events.emit("error", { error, apiUrlCreator });
				return false;
			}
		}

		await PublishEmitter.events.emit("finish", {});
		return true;
	},

	events: createEventEmitter<PublishServiceEvents>(),
};
